import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiPlus, FiSearch, FiSlash } from 'react-icons/fi';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { getFlagUrl } from '../utilities/flags';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { IfCanWrite } from '../utilities/adminAccess';
import {
  extractWindows,
  formatWindowWeekdays,
  isSeasonWindowOpen,
  isTransferWindowEnabled,
  leagueFlagValue,
} from '../utilities/transferWindows';
import ReorderableTable from '../components/ReorderableTable';
import StadiumCrumbs from '../components/StadiumCrumbs';
import '../styles/stadiumsManagement.css';
import '../styles/disqualificationsManagement.css';
import '../styles/tournamentsManagement.css';
import '../styles/transferWindows.css';

function LeagueFlag({ flag }) {
  const [failed, setFailed] = useState(false);
  const src = getFlagUrl(flag);

  if (!src || failed) return null;

  return (
    <img
      src={src}
      alt=""
      className="dq-league-flag"
      onError={() => setFailed(true)}
    />
  );
}

function seasonWindowStatus(item, t) {
  if (item?.active_season_id == null) {
    return { className: 'disabled', label: t('twSeasonNone') };
  }
  if (isSeasonWindowOpen(item)) {
    return { className: 'enabled', label: t('twSeasonOpen') };
  }
  return { className: 'disabled', label: t('twSeasonClosed') };
}

export default function TransferWindowsManagement() {
  const dispatch = useDispatch();
  const { t } = useI18n();
  const { champId, loading: champsLoading } = useChampionships();

  const [windows, setWindows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [message, setMessage] = useState({ text: '', type: '' });
  const [mobileTab, setMobileTab] = useState('schedule');
  const searchTimer = useRef(null);

  useEffect(() => {
    if (!champId && champsLoading) return undefined;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const delay = searchQuery ? 350 : 0;
    searchTimer.current = setTimeout(() => {
      loadWindows(searchQuery);
    }, delay);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchQuery, champId, champsLoading]);

  const loadWindows = async (search) => {
    if (!champId) {
      setWindows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setMessage({ text: '', type: '' });
    try {
      const body = { champ_id: champId };
      if (search && search.trim()) body.search = search.trim();
      const response = await authorizedPost(dispatch, API_URLS.getAdminTransferWindows, body);
      setWindows(extractWindows(response.data));
    } catch (error) {
      setWindows([]);
      setMessage({ text: getApiErrorMessage(error, t('twLoadError')), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const scheduledWindows = windows.filter(isTransferWindowEnabled);

  const scheduleColumns = [
    {
      key: 'enabled',
      label: t('twStatus'),
      className: 'st-cell-status',
      render: () => (
        <span className="st-badge enabled">{t('twEnabled')}</span>
      ),
    },
    {
      key: 'name',
      label: t('twLeague'),
      className: 'st-cell-name',
      render: (item) => (
        <span className="dq-league">
          <LeagueFlag flag={leagueFlagValue(item)} />
          <span>{item.name || '—'}</span>
        </span>
      ),
    },
    {
      key: 'period',
      label: t('twPeriod'),
      className: 'tw-cell-period st-cell-muted',
      render: (item) => <span className="tw-datetime">{formatWindowWeekdays(item, t)}</span>,
    },
  ];

  const seasonColumns = [
    {
      key: 'status',
      label: t('twStatus'),
      className: 'st-cell-status',
      render: (item) => {
        const status = seasonWindowStatus(item, t);
        return <span className={`st-badge ${status.className}`}>{status.label}</span>;
      },
    },
    {
      key: 'name',
      label: t('twLeague'),
      className: 'st-cell-name',
      render: (item) => (
        <span className="dq-league">
          <LeagueFlag flag={leagueFlagValue(item)} />
          <span>{item.name || '—'}</span>
        </span>
      ),
    },
    {
      key: 'season',
      label: t('twSeasonName'),
      className: 'tw-cell-season st-cell-muted',
      render: (item) => item.active_season_name || '—',
    },
  ];

  const loadingBlock = (
    <div className="st-loading">
      <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
      <p>{t('twLoading')}</p>
    </div>
  );

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page dq-page tw-list-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs items={[{ label: t('navTransferWindows') }]} />
          </div>
        </div>

        <div className="st-toolbar">
          <div className="st-search">
            <FiSearch className="st-search-icon" />
            <input
              type="text"
              className="st-search-input"
              placeholder={t('twSearchPh')}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
            {searchQuery ? (
              <button type="button" className="st-search-clear" onClick={() => setSearchQuery('')}>
                ×
              </button>
            ) : null}
          </div>
        </div>

        {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

        <div className="tw-mobile-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mobileTab === 'schedule'}
            className={`tw-mobile-tab ${mobileTab === 'schedule' ? 'is-active' : ''}`}
            onClick={() => setMobileTab('schedule')}
          >
            {t('twScheduleTitle')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mobileTab === 'season'}
            className={`tw-mobile-tab ${mobileTab === 'season' ? 'is-active' : ''}`}
            onClick={() => setMobileTab('season')}
          >
            {t('twSeasonTitle')}
          </button>
        </div>

        <div className="st-scroll dq-scroll">
          <div className="dq-split">
            <section className={`st-block dq-panel ${mobileTab === 'schedule' ? 'is-active-tab' : ''}`}>
              <h2 className="st-block-header">{t('twScheduleTitle')}</h2>
              <div className="st-meta tw-panel-meta">
                <strong>{t('twFound', { count: scheduledWindows.length })}</strong>
              </div>
              <div className="dq-panel-body">
                {loading ? loadingBlock : (
                  <ReorderableTable
                    tableClassName="st-table-tw"
                    columns={scheduleColumns}
                    items={scheduledWindows}
                    getRowKey={(item) => item.id}
                    reorderMode={false}
                    onItemsChange={() => {}}
                    getRowHref={(item) => (item.id ? `/transfer_windows/${item.id}` : null)}
                    empty={(
                      <div className="st-empty">
                        <FiSlash size={48} />
                        <h3>{t('twEmpty')}</h3>
                        <p>{t('twEmptyHint')}</p>
                      </div>
                    )}
                  />
                )}
              </div>
              <div className="st-actions tw-panel-actions">
                <IfCanWrite entity="leagues">
                  <Link to="/transfer_windows/new" className="st-btn st-btn-primary">
                    <FiPlus />
                    <span className="tr-btn-full">{t('twAdd')}</span>
                    <span className="tr-btn-short">{t('twAddShort')}</span>
                  </Link>
                </IfCanWrite>
              </div>
            </section>

            <section className={`st-block dq-panel ${mobileTab === 'season' ? 'is-active-tab' : ''}`}>
              <h2 className="st-block-header">{t('twSeasonTitle')}</h2>
              <p className="st-hint tw-panel-hint">{t('twSeasonHint')}</p>
              <div className="st-meta tw-panel-meta">
                <strong>{t('twFound', { count: windows.length })}</strong>
              </div>
              <div className="dq-panel-body">
                {loading ? loadingBlock : (
                  <ReorderableTable
                    tableClassName="st-table-tw-season"
                    columns={seasonColumns}
                    items={windows}
                    getRowKey={(item) => `season-${item.id}`}
                    reorderMode={false}
                    onItemsChange={() => {}}
                    getRowHref={(item) => (item.id ? `/transfer_windows/seasons/${item.id}` : null)}
                    empty={(
                      <div className="st-empty">
                        <FiSlash size={48} />
                        <h3>{t('twSeasonEmpty')}</h3>
                        <p>{t('twSeasonEmptyHint')}</p>
                      </div>
                    )}
                  />
                )}
              </div>
            </section>
          </div>
        </div>

        <div className={`st-actions tw-page-actions ${mobileTab === 'schedule' ? 'is-visible' : ''}`}>
          <IfCanWrite entity="leagues">
            <Link to="/transfer_windows/new" className="st-btn st-btn-primary">
              <FiPlus />
              <span className="tr-btn-full">{t('twAdd')}</span>
              <span className="tr-btn-short">{t('twAddShort')}</span>
            </Link>
          </IfCanWrite>
        </div>
      </div>
    </div>
  );
}
